import React, { useMemo } from 'react';

import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import BedIcon from '@mui/icons-material/Bed';
import RedeemIcon from '@mui/icons-material/Redeem';
import { Alert, Box, Chip, Stack, Tab, Tabs, Typography } from '@mui/material';

import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { isLTLEHotelOfferType } from '~/utils/offer';

import InclusionAssetsTab from './InclusionAssetsTab';
import RoomAssetsTab from './RoomAssetsTab';
import { INCLUSION_ASSETS_TAB, ROOM_ASSETS_TAB, getAssetsTab } from './constants';
import type { AssetsTab } from './constants';
import { getUniqueRoomAssets } from './utils';

const TAB_SUBTITLES: Record<string, string> = {
  [ROOM_ASSETS_TAB]:
    'Select a room asset to inspect synced room, bedding, occupancy, rack rate, and system information.',
  [INCLUSION_ASSETS_TAB]:
    'Select an inclusion asset to inspect synced category, pricing, description, and system information.',
};

export default function OfferBuilderAssetsPage() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const selectedTab = getAssetsTab(searchParams.get('tab'));
  const { data: offer, isLoading: isLoadingOffer, error: offerError } = useGetOffer(offerId);
  const isSupportedOfferType = !!offer?.type && isLTLEHotelOfferType(offer.type);
  const hasOfferLoadError = !isLoadingOffer && !!offerError && !offer;
  const {
    data: opportunityRelated,
    isLoading: isLoadingRelated,
    error: relatedError,
  } = useGetOpportunityRelated(offerId, { enabled: isSupportedOfferType });
  const roomAssets = useMemo(() => getUniqueRoomAssets(opportunityRelated?.deals), [opportunityRelated?.deals]);
  const inclusions = opportunityRelated?.inclusions ?? [];
  const hotelName = offer?.name || 'Property';

  // The inclusion selection and drill-down live in the query string too, so leaving the tab has to
  // clear them or returning to it would restore a selection the list no longer shows.
  const handleTabChange = (tab: AssetsTab) => {
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set('tab', tab);

    if (tab !== INCLUSION_ASSETS_TAB) {
      nextSearchParams.delete('inclusion');
      nextSearchParams.delete('view');
    }

    navigate({ pathname: location.pathname, search: nextSearchParams.toString() });
  };

  return (
    <>
      <Helmet>
        <title>Assets | {hotelName}</title>
      </Helmet>

      {hasOfferLoadError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Unable to load offer details.
        </Alert>
      )}

      {!isLoadingOffer && isSupportedOfferType && (
        <>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Assets
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Review sellable assets synced from Salesforce.
          </Typography>

          <Tabs value={selectedTab} onChange={(_event, value) => handleTabChange(value)} sx={{ mb: 1 }}>
            <Tab
              value={ROOM_ASSETS_TAB}
              icon={<BedIcon fontSize="small" />}
              iconPosition="start"
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>Room Assets</span>
                  <Chip label={roomAssets.length} size="small" />
                </Stack>
              }
            />
            <Tab
              value={INCLUSION_ASSETS_TAB}
              icon={<RedeemIcon fontSize="small" />}
              iconPosition="start"
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>Inclusion Assets</span>
                  <Chip label={inclusions.length} size="small" />
                </Stack>
              }
            />
          </Tabs>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {TAB_SUBTITLES[selectedTab]}
            </Typography>
          </Box>

          {relatedError ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              Unable to load synced assets.
            </Alert>
          ) : selectedTab === ROOM_ASSETS_TAB ? (
            <RoomAssetsTab deals={opportunityRelated?.deals} isLoading={isLoadingRelated} />
          ) : (
            <InclusionAssetsTab inclusions={inclusions} isLoading={isLoadingRelated} />
          )}
        </>
      )}
    </>
  );
}
